import { useState } from "react";
import Image from "@Components/Image";
import useTranslationHook from "hooks/useTranslationHook";
import { getRandomAvatar } from "@Pages/PropertyPage/components/ReviewsSectionV2/avatars";
import {
  ANONYMOUS_REVIEWER_KEY,
  isAnonymousReview,
} from "@Pages/PropertyPage/components/ReviewsSectionV2/anonymousReviewer";
import styles from "../ReviewsSectionStyles.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";

interface VideoReviewCardProps {
  review: any;
  getTimeText?: (review: any) => string;
}

const VideoReviewCard = ({ review, getTimeText }: VideoReviewCardProps) => {
  const [showFull, setShowFull] = useState<boolean>(false);
  const { t } = useTranslationHook();

  const handleToggleReview = () => {
    setShowFull((val) => !val);
  };

  const anonymous = isAnonymousReview(review);
  const name = anonymous
    ? t(ANONYMOUS_REVIEWER_KEY)
    : review?.user_details?.name || review?.name || "";
  const text = review?.content || review?.review || review?.text || "";
  const rating = review?.rating;
  const profilePicture = anonymous ? getRandomAvatar("", 0) : review?.profile_picture?.path || "";

  const initials =
    name.trim().split(" ").length > 1
      ? `${name.trim().split(" ")[0]?.[0] || ""}${
          name.trim().split(" ")[name.trim().split(" ").length - 1]?.[0] || ""
        }`
      : name.trim().split(" ")[0]?.[0] || "";

  const charLimit = 150;
  const isLong = text.length > charLimit;
  const displayedContent = !isLong || showFull ? text : `${text.slice(0, charLimit)}..`;

  return (
    <div className={styles.videoReviewCard}>
      <div className={styles.videoReviewCardTop}>
        <div className={styles.videoReviewerInfo}>
          {profilePicture ? (
            <Image
              className={styles.videoAvatar}
              src={profilePicture}
              alt={name}
              width={36}
              height={36}
            />
          ) : (
            <div className={styles.videoAvatar}>{initials}</div>
          )}
          <div>
            <div className={styles.videoReviewerName}>{name}</div>
            {getTimeText && <div className={styles.videoReviewDate}>{getTimeText(review)}</div>}
          </div>
        </div>
        {rating && (
          <div className={styles.videoReviewRating}>
            {parseFloat(rating).toFixed(1)}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M6 1L7.545 4.13L11 4.635L8.5 7.07L9.09 10.51L6 8.885L2.91 10.51L3.5 7.07L1 4.635L4.455 4.13L6 1Z"
                fill="#ffffff"
              />
            </svg>
          </div>
        )}
      </div>
      {text && (
        <div className={styles.videoReviewText}>
          {displayedContent}{" "}
          {isLong && (
            <span
              role="button"
              tabIndex={0}
              className={styles.videoReadMore}
              onClick={handleToggleReview}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleToggleReview();
              }}
            >
              {showFull
                ? t("propertypage.reviewssection.readless")
                : t("propertypage.reviewssection.readmore")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(VideoReviewCard, {
  componentName: "VideoReviewCard-ReviewsSection-PropertyPage",
  showForChina: true,
});
